import { inject, Injectable } from '@angular/core';
import { BooleanOperator, ComparisonOperator } from '@common/enums';
import { DriveOdataItem } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { DriveTreeOdataService } from '@common/services';
import { CustomTreeElement, DriveTreeElement } from '@common/types';
import { createTreeFromDrives, updateTotalChildren } from '@common/utils';
import { TreeComponent, TreeElement } from 'mbs-ui-kit';
import { defer, Observable } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

@Injectable()
export class DriveTreeHelperService {
  private readonly treeService = inject(DriveTreeOdataService);

  requestHandlerTree(
    treeElem: DriveTreeElement,
    update: boolean,
    setLoadingFn: (val: boolean) => void
  ): (source: Observable<ODataPagedResult<DriveOdataItem>>) => Observable<DriveTreeElement[]> {
    return (source: Observable<ODataPagedResult<DriveOdataItem>>): Observable<DriveTreeElement[]> => {
      return defer(() => {
        setLoadingFn(true);

        return source.pipe(
          take(1),
          map(({ data, count }) => {
            return {
              node: createTreeFromDrives(data),
              count
            };
          }),
          updateTotalChildren<DriveTreeElement>(treeElem, update),
          finalize(() => setLoadingFn(false))
        );
      });
    };
  }

  getOdataIdFilter(path: string): string {
    const neTypeStr = `Type ${ComparisonOperator.Eq} false`;

    return path ? `Id ${ComparisonOperator.Eq} ${path} ${BooleanOperator.And} ${neTypeStr}` : neTypeStr;
  }

  getRoot(item: TreeElement): TreeElement {
    let temp = item;

    while (temp.parent) {
      temp = temp.parent;
    }

    return temp;
  }

  findChildByPath(root: CustomTreeElement<any>, path: string): CustomTreeElement<any> {
    return root.children?.find((treeEl) => treeEl.id == path);
  }

  getTreeElemLoadedPage(treeElem: TreeElement): number {
    const pageSize = this.treeService.pageSize;
    const loadedCount = treeElem.children?.length || 0;
    const totalChildren = treeElem.totalChildren ?? 0;

    if (!pageSize || !totalChildren || !loadedCount || pageSize > totalChildren) return 1;

    return Math.floor(loadedCount / pageSize) + 1;
  }

  unselectAllTreeItems(treeComponent: TreeComponent) {
    treeComponent.getSelected().forEach((el) => treeComponent.doSelect(false, el));
  }
}
